import { create } from 'zustand';
import { supabase, DBItem, DBStockUnit, DBStockLog } from '../lib/supabase';
import { getActedByName } from './authStore';
import { getActiveCollectionId } from './collectionStore';
import { useWarehouseStore } from './warehouseStore';

// ── Domain types ──────────────────────────────────────────────────────────────

export interface Item {
  id: string;
  name: string;
  sku: string | null;
  category: string | null;
  notes: string | null;
  requiresSerial: boolean;
  requiresMac: boolean;
  isDiscontinued: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface StockUnit {
  id: string;
  itemId: string;
  warehouseId: string;
  storageAreaId: string | null;
  unitId: number;
  serialMac: string | null;
  condition: 'new' | 'used' | null;
  status: string;
  reference: string | null;
  owner: string | null;
  notes: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface StockLog {
  id: string;
  stockUnitId: string;
  eventType: string;
  description: string | null;
  reference: string | null;
  movementType: string | null;
  fromWarehouseId: string | null;
  fromStorageAreaId: string | null;
  toWarehouseId: string | null;
  toStorageAreaId: string | null;
  actedBy: string | null;
  createdAt: Date;
}

export type NewItem = Omit<Item, 'id' | 'createdAt' | 'updatedAt' | 'isDiscontinued'>;
export type ItemUpdates = Partial<NewItem> & { isDiscontinued?: boolean };
export type NewStockUnit = Omit<StockUnit, 'id' | 'unitId' | 'createdAt' | 'updatedAt'>;

/** Combine serial + MAC the same way stock receipt does (`format_receipt_serial_mac`). */
export function formatReceiptSerialMac(
  serial: string | null | undefined,
  mac: string | null | undefined,
  requiresSerial: boolean,
  requiresMac: boolean,
): string | null {
  const s = serial?.trim() || null;
  const m = mac?.trim() || null;
  if (requiresSerial && requiresMac) {
    if (!s && !m) return null;
    if (s && m) return `S:${s} M:${m}`;
    return s ?? m;
  }
  if (requiresMac) return m;
  if (requiresSerial) return s;
  return s ?? m;
}

/** Catalogue pickers: active items only, plus an already-selected discontinued id. */
export function cataloguePickerItems(items: Item[], keepId?: string | null): Item[] {
  return items.filter(i => !i.isDiscontinued || (keepId != null && i.id === keepId));
}

/** True when local stock cache already references this catalogue item. */
export function itemHasLocalStock(itemId: string, stockUnits: StockUnit[]): boolean {
  return stockUnits.some(u => u.itemId === itemId);
}

async function checkItemInUse(itemId: string): Promise<boolean> {
  const [unitsRes, linesRes] = await Promise.all([
    supabase.from('stock_units').select('id').eq('item_id', itemId).limit(1),
    supabase
      .from('stock_request_lines')
      .select('id')
      .or(`item_id.eq.${itemId},substitute_item_id.eq.${itemId}`)
      .limit(1),
  ]);
  return (unitsRes.data?.length ?? 0) > 0 || (linesRes.data?.length ?? 0) > 0;
}

export interface LogOptions {
  movementType?: string;
  fromWarehouseId?: string | null;
  fromStorageAreaId?: string | null;
  toWarehouseId?: string | null;
  toStorageAreaId?: string | null;
}

export interface UpdateStockUnitOptions {
  skipLog?: boolean;
}

export interface LogEntry {
  stockUnitId: string;
  eventType: string;
  description: string;
  reference?: string;
  options?: LogOptions;
}

// ── Store interface ───────────────────────────────────────────────────────────

interface InventoryState {
  items: Item[];
  stockUnits: StockUnit[];
  recentLogs: StockLog[];
  isLoadingItems: boolean;
  isLoadingUnits: boolean;
  error: string | null;

  // Items (catalogue)
  fetchItems: () => Promise<void>;
  addItem: (item: NewItem) => Promise<Item | null>;
  updateItem: (id: string, updates: ItemUpdates) => Promise<boolean>;
  renameCategory: (from: string, to: string) => Promise<boolean>;
  setItemDiscontinued: (id: string, isDiscontinued: boolean) => Promise<boolean>;
  removeItem: (id: string) => Promise<boolean>;
  isItemInUse: (id: string) => Promise<boolean>;

  // Stock units
  fetchStockUnits: (warehouseId?: string, storageAreaId?: string) => Promise<void>;
  addStockUnit: (unit: NewStockUnit) => Promise<StockUnit | null>;
  addStockUnits: (units: NewStockUnit[], logDescription?: string) => Promise<StockUnit[]>;
  updateStockUnit: (
    id: string,
    updates: Partial<NewStockUnit>,
    logDescription?: string,
    logOptions?: LogOptions,
    opts?: UpdateStockUnitOptions,
  ) => Promise<void>;
  removeStockUnit: (id: string) => Promise<void>;

  // Logs
  fetchLogs: (stockUnitId: string) => Promise<StockLog[]>;
  fetchRecentLogs: (warehouseId?: string, limit?: number) => Promise<void>;
  addLog: (stockUnitId: string, eventType: string, description: string, reference?: string, options?: LogOptions) => Promise<void>;
  addLogsBatch: (entries: LogEntry[]) => Promise<void>;
}

// ── Mappers ───────────────────────────────────────────────────────────────────

const toItem = (row: DBItem): Item => ({
  id: row.id,
  name: row.name,
  sku: row.sku,
  category: row.category,
  notes: row.notes,
  requiresSerial: row.requires_serial ?? false,
  requiresMac: row.requires_mac ?? false,
  isDiscontinued: row.is_discontinued ?? false,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
});

const toStockUnit = (row: DBStockUnit): StockUnit => ({
  id: row.id,
  itemId: row.item_id,
  warehouseId: row.warehouse_id,
  storageAreaId: row.storage_area_id,
  unitId: row.unit_id,
  serialMac: row.serial_mac,
  condition: row.condition,
  status: row.status,
  reference: row.reference,
  owner: row.owner,
  notes: row.notes,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
});

const toLog = (row: DBStockLog): StockLog => ({
  id: row.id,
  stockUnitId: row.stock_unit_id,
  eventType: row.event_type,
  description: row.description,
  reference: row.reference,
  movementType: row.movement_type,
  fromWarehouseId: row.from_warehouse_id,
  fromStorageAreaId: row.from_storage_area_id,
  toWarehouseId: row.to_warehouse_id,
  toStorageAreaId: row.to_storage_area_id,
  actedBy: row.acted_by ?? null,
  createdAt: new Date(row.created_at),
});

// ── Store ─────────────────────────────────────────────────────────────────────

const REQUEST_TIMEOUT_MS = 20_000;

function withTimeout<T>(promise: PromiseLike<T>, ms: number): Promise<T> {
  return Promise.race([
    Promise.resolve(promise),
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), ms),
    ),
  ]);
}

let itemsFetchGen = 0;
let unitsFetchGen = 0;

export const useInventoryStore = create<InventoryState>((set, get) => ({
  items: [],
  stockUnits: [],
  recentLogs: [],
  isLoadingItems: false,
  isLoadingUnits: false,
  error: null,

  // ── Items ──

  fetchItems: async () => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) {
      set({ items: [], isLoadingItems: false });
      return;
    }

    const gen = ++itemsFetchGen;
    set({ isLoadingItems: true, error: null });
    try {
      const { data, error } = await supabase
        .from('items')
        .select('*')
        .eq('collection_id', collectionId)
        .order('name', { ascending: true });
      if (gen !== itemsFetchGen) return;
      if (error) {
        set({ isLoadingItems: false, error: error.message });
        return;
      }
      set({ items: (data ?? []).map(toItem), isLoadingItems: false });
    } catch (err) {
      if (gen !== itemsFetchGen) return;
      set({
        isLoadingItems: false,
        error: err instanceof Error ? err.message : 'Failed to load items',
      });
    }
  },

  addItem: async (item: NewItem) => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) {
      set({ error: 'No collection loaded' });
      return null;
    }

    const { data, error } = await supabase
      .from('items')
      .insert({
        name: item.name.trim(),
        sku: item.sku || null,
        category: item.category || null,
        notes: item.notes || null,
        requires_serial: item.requiresSerial ?? false,
        requires_mac: item.requiresMac ?? false,
        is_discontinued: false,
        collection_id: collectionId,
      })
      .select()
      .single();
    if (error || !data) { set({ error: error?.message ?? 'Failed to add item' }); return null; }
    const newItem = toItem(data as DBItem);
    set(s => ({ items: [...s.items, newItem].sort((a, b) => a.name.localeCompare(b.name)) }));
    return newItem;
  },

  isItemInUse: async (id: string) => checkItemInUse(id),

  updateItem: async (id: string, updates: ItemUpdates) => {
    const existing = get().items.find(i => i.id === id);
    const dbUpdates: Record<string, unknown> = {};
    let blockedIdentity = false;

    const nextName = updates.name !== undefined ? updates.name.trim() : undefined;
    const nextSku = updates.sku !== undefined ? (updates.sku || null) : undefined;
    const nameChanging = nextName !== undefined && existing != null && nextName !== existing.name;
    const skuChanging = nextSku !== undefined && existing != null && nextSku !== (existing.sku ?? null);

    if (nameChanging || skuChanging) {
      const inUse = await checkItemInUse(id);
      if (inUse) {
        blockedIdentity = true;
      } else {
        if (nextName !== undefined) dbUpdates.name = nextName;
        if (nextSku !== undefined) dbUpdates.sku = nextSku;
      }
    } else {
      if (nextName !== undefined) dbUpdates.name = nextName;
      if (nextSku !== undefined) dbUpdates.sku = nextSku;
    }

    if (updates.category !== undefined) dbUpdates.category = updates.category || null;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes || null;
    if (updates.requiresSerial !== undefined) dbUpdates.requires_serial = updates.requiresSerial;
    if (updates.requiresMac !== undefined) dbUpdates.requires_mac = updates.requiresMac;
    if (updates.isDiscontinued !== undefined) dbUpdates.is_discontinued = updates.isDiscontinued;

    if (Object.keys(dbUpdates).length === 0) {
      if (blockedIdentity) {
        set({ error: 'Name and SKU cannot be changed once this item has been used.' });
      }
      return false;
    }

    const { data, error } = await supabase.from('items').update(dbUpdates).eq('id', id).select().single();
    if (error || !data) {
      set({ error: error?.message ?? 'Failed to update item' });
      return false;
    }
    const updated = toItem(data as DBItem);
    set(s => ({ items: s.items.map(i => i.id === id ? updated : i), error: null }));
    return true;
  },

  renameCategory: async (from, to) => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) {
      set({ error: 'No collection loaded' });
      return false;
    }
    const oldName = from.trim();
    const newName = to.trim();
    if (!oldName || !newName) {
      set({ error: 'Category name is required.' });
      return false;
    }
    if (oldName === newName) return true;

    const { error } = await supabase
      .from('items')
      .update({ category: newName })
      .eq('collection_id', collectionId)
      .eq('category', oldName);
    if (error) {
      set({ error: error.message });
      return false;
    }

    set(s => ({
      items: s.items.map(i => (i.category === oldName ? { ...i, category: newName, updatedAt: new Date() } : i)),
      error: null,
    }));
    return true;
  },

  setItemDiscontinued: async (id: string, isDiscontinued: boolean) => {
    const { data, error } = await supabase
      .from('items')
      .update({ is_discontinued: isDiscontinued })
      .eq('id', id)
      .select()
      .single();
    if (error || !data) {
      set({ error: error?.message ?? 'Failed to update item' });
      return false;
    }
    const updated = toItem(data as DBItem);
    set(s => ({ items: s.items.map(i => i.id === id ? updated : i), error: null }));
    return true;
  },

  removeItem: async (id: string) => {
    if (await checkItemInUse(id)) {
      set({ error: 'Cannot delete an item that has stock or request history. Discontinue it instead.' });
      return false;
    }
    const { error } = await supabase.from('items').delete().eq('id', id);
    if (error) {
      set({ error: error.message });
      return false;
    }
    set(s => ({ items: s.items.filter(i => i.id !== id), error: null }));
    return true;
  },

  // ── Stock units ──

  fetchStockUnits: async (warehouseId?: string, storageAreaId?: string) => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) {
      set({ stockUnits: [], isLoadingUnits: false });
      return;
    }

    const gen = ++unitsFetchGen;
    set({ isLoadingUnits: true, error: null });
    try {
      let query = supabase.from('stock_units').select('*').order('unit_id', { ascending: true });
      if (warehouseId) {
        query = query.eq('warehouse_id', warehouseId);
      } else {
        const warehouseIds = useWarehouseStore.getState().warehouses.map((w) => w.id);
        if (warehouseIds.length === 0) {
          if (gen !== unitsFetchGen) return;
          set({ stockUnits: [], isLoadingUnits: false });
          return;
        }
        query = query.in('warehouse_id', warehouseIds);
      }
      if (storageAreaId) query = query.eq('storage_area_id', storageAreaId);

      const { data, error } = await withTimeout(query, REQUEST_TIMEOUT_MS);
      if (gen !== unitsFetchGen) return;
      if (error) {
        set({ isLoadingUnits: false, error: error.message });
        return;
      }
      set({ stockUnits: (data ?? []).map(toStockUnit), isLoadingUnits: false });
    } catch (err) {
      if (gen !== unitsFetchGen) return;
      set({
        isLoadingUnits: false,
        error: err instanceof Error ? err.message : 'Failed to load stock units',
      });
    }
  },

  addStockUnit: async (unit: NewStockUnit) => {
    const { data, error } = await supabase
      .from('stock_units')
      .insert({
        item_id: unit.itemId,
        warehouse_id: unit.warehouseId,
        storage_area_id: unit.storageAreaId,
        serial_mac: unit.serialMac || null,
        condition: unit.condition,
        status: unit.status,
        reference: unit.reference || null,
        owner: unit.owner || null,
        notes: unit.notes || null,
      })
      .select()
      .single();
    if (error || !data) return null;
    const newUnit = toStockUnit(data as DBStockUnit);
    set(s => ({ stockUnits: [...s.stockUnits, newUnit] }));
    await get().addLog(newUnit.id, 'Added', 'Stock unit added', unit.reference ?? undefined, { movementType: 'add_stock', toWarehouseId: unit.warehouseId, toStorageAreaId: unit.storageAreaId });
    return newUnit;
  },

  addStockUnits: async (units: NewStockUnit[], logDescription = 'Stock unit added') => {
    if (units.length === 0) return [];
    const rows = units.map(unit => ({
      item_id: unit.itemId,
      warehouse_id: unit.warehouseId,
      storage_area_id: unit.storageAreaId,
      serial_mac: unit.serialMac || null,
      condition: unit.condition,
      status: unit.status,
      reference: unit.reference || null,
      owner: unit.owner || null,
      notes: unit.notes || null,
    }));
    const { data, error } = await withTimeout(
      supabase.from('stock_units').insert(rows).select(),
      REQUEST_TIMEOUT_MS,
    );
    if (error || !data) return [];
    const newUnits = (data as DBStockUnit[]).map(toStockUnit);
    set(s => ({ stockUnits: [...s.stockUnits, ...newUnits] }));

    const actedBy = getActedByName();
    const logRows = newUnits.map((u, i) => ({
      stock_unit_id: u.id,
      event_type: 'Added',
      description: logDescription,
      reference: units[i]?.reference ?? null,
      movement_type: 'add_stock',
      from_warehouse_id: null,
      from_storage_area_id: null,
      to_warehouse_id: u.warehouseId,
      to_storage_area_id: u.storageAreaId,
      acted_by: actedBy,
    }));
    const { data: logData } = await withTimeout(
      supabase.from('stock_logs').insert(logRows).select(),
      REQUEST_TIMEOUT_MS,
    );
    if (logData && logData.length > 0) {
      const logs = (logData as DBStockLog[]).map(toLog);
      const limit = 50;
      set(s => {
        const merged = [...logs, ...s.recentLogs.filter(l => !logs.some(nl => nl.id === l.id))];
        merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        return { recentLogs: merged.slice(0, limit) };
      });
    }
    return newUnits;
  },

  updateStockUnit: async (id, updates, logDescription?, logOptions?, opts?) => {
    const dbUpdates: Record<string, any> = {};
    if (updates.itemId !== undefined) dbUpdates.item_id = updates.itemId;
    if (updates.warehouseId !== undefined) dbUpdates.warehouse_id = updates.warehouseId;
    if (updates.storageAreaId !== undefined) dbUpdates.storage_area_id = updates.storageAreaId;
    if (updates.serialMac !== undefined) dbUpdates.serial_mac = updates.serialMac || null;
    if (updates.condition !== undefined) dbUpdates.condition = updates.condition;
    if (updates.status !== undefined) dbUpdates.status = updates.status;
    if (updates.reference !== undefined) dbUpdates.reference = updates.reference || null;
    if (updates.owner !== undefined) dbUpdates.owner = updates.owner || null;
    if (updates.notes !== undefined) dbUpdates.notes = updates.notes || null;

    const { data, error } = await withTimeout(
      supabase.from('stock_units').update(dbUpdates).eq('id', id).select().single(),
      REQUEST_TIMEOUT_MS,
    );
    if (error || !data) return;
    const updated = toStockUnit(data as DBStockUnit);
    set(s => ({ stockUnits: s.stockUnits.map(u => u.id === id ? updated : u) }));
    if (logDescription && !opts?.skipLog) await get().addLog(id, 'Edited', logDescription, undefined, logOptions);
  },

  removeStockUnit: async (id: string) => {
    await supabase.from('stock_units').delete().eq('id', id);
    set(s => ({ stockUnits: s.stockUnits.filter(u => u.id !== id) }));
  },

  // ── Logs ──

  fetchRecentLogs: async (_warehouseId?: string, limit = 50) => {
    try {
      const warehouseIds = useWarehouseStore.getState().warehouses.map((w) => w.id);
      if (warehouseIds.length === 0) {
        set({ recentLogs: [] });
        return;
      }
      const { data } = await withTimeout(
        supabase
          .from('stock_logs')
          .select('*, stock_units!inner(warehouse_id)')
          .in('stock_units.warehouse_id', warehouseIds)
          .order('created_at', { ascending: false })
          .limit(limit),
        REQUEST_TIMEOUT_MS,
      );
      set({ recentLogs: (data ?? []).map(toLog) });
    } catch {
      // Non-fatal — widget will just show stale data
    }
  },

  fetchLogs: async (stockUnitId: string) => {
    try {
      const { data } = await withTimeout(
        supabase.from('stock_logs').select('*').eq('stock_unit_id', stockUnitId).order('created_at', { ascending: false }),
        REQUEST_TIMEOUT_MS,
      );
      return (data ?? []).map(toLog);
    } catch {
      return [];
    }
  },

  addLog: async (stockUnitId: string, eventType: string, description: string, reference?: string, options?: LogOptions) => {
    await get().addLogsBatch([{ stockUnitId, eventType, description, reference, options }]);
  },

  addLogsBatch: async (entries: LogEntry[]) => {
    if (entries.length === 0) return;
    const actedBy = getActedByName();
    const rows = entries.map(e => ({
      stock_unit_id: e.stockUnitId,
      event_type: e.eventType,
      description: e.description,
      reference: e.reference ?? null,
      movement_type: e.options?.movementType ?? null,
      from_warehouse_id: e.options?.fromWarehouseId ?? null,
      from_storage_area_id: e.options?.fromStorageAreaId ?? null,
      to_warehouse_id: e.options?.toWarehouseId ?? null,
      to_storage_area_id: e.options?.toStorageAreaId ?? null,
      acted_by: actedBy,
    }));
    const { data, error } = await withTimeout(
      supabase.from('stock_logs').insert(rows).select(),
      REQUEST_TIMEOUT_MS,
    );
    if (error || !data) return;
    const logs = (data as DBStockLog[]).map(toLog);
    const limit = 50;
    set(s => {
      const merged = [...logs, ...s.recentLogs.filter(l => !logs.some(nl => nl.id === l.id))];
      merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      return { recentLogs: merged.slice(0, limit) };
    });
  },
}));
